import type { OperationEnvelope, OperationName } from "@/lib/operations-contract";
import { RegistrationError } from "@/lib/app-error";

/* Browser-side transport for the F360 operation route.

   Every business call goes to POST /api/f360/<operation> on this origin.
   The browser never names a Frappe method, never picks a verb, and only
   ever sees the normalized `{ok, data}` / `{ok, status, detail}` envelope.

   Failures are raised as RegistrationError with code "UNKNOWN", the
   upstream status and the backend's human-readable detail; the service
   layer maps that pair onto the AppErrorCode taxonomy exactly as it did
   for SDK errors. */

export class OperationError extends RegistrationError {
  constructor(status: number, detail: string | undefined, public requestId: string) {
    super("UNKNOWN", status, detail);
    this.name = "OperationError";
  }
}

export async function callOperation<T = unknown>(operation: OperationName, input: unknown = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api/f360/${operation}`, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(input ?? {}),
    });
  } catch {
    throw new OperationError(0, undefined, "");
  }

  let envelope: OperationEnvelope<T> | null = null;
  try {
    envelope = (await response.json()) as OperationEnvelope<T>;
  } catch {
    envelope = null;
  }

  if (!envelope || typeof envelope !== "object" || !("ok" in envelope)) {
    throw new OperationError(response.status || 0, undefined, response.headers.get("x-request-id") ?? "");
  }
  if (!envelope.ok) throw new OperationError(envelope.status || response.status || 0, envelope.detail, envelope.requestId);
  return envelope.data;
}
